import { useId, useMemo } from 'react';
import {
    Area,
    AreaChart as RechartsAreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { MultiSeriesChartTooltip } from './MultiSeriesChartTooltip';

export interface MultiSeriesChartDataPoint {
    date: string;
    startTime?: number;
    endTime?: number;
    [key: string]: string | number | undefined | null;
}

export interface MultiSeriesChartSeries {
    dataKey: string;
    label: string;
    color: string;
    dimmed?: boolean;
}

export interface MultiSeriesChartProps {
    data: MultiSeriesChartDataPoint[];
    series: MultiSeriesChartSeries[];
    height?: number;
    showGrid?: boolean;
    margin?: { top?: number; right?: number; bottom?: number; left?: number };
    valueFormatter?: (value: number) => string;
    xAxisTickFormatter?: (value: string) => string;
    yAxisTickFormatter?: (value: number) => string;
    xAxisInterval?:
        | number
        | 'preserveStart'
        | 'preserveEnd'
        | 'preserveStartEnd';
}

const DIMMED_OPACITY = 0.15;
const AXIS_TICK_COUNT = 5;

export function MultiSeriesChart({
    data,
    series,
    height = 298,
    showGrid = true,
    margin = { top: 10, right: 0, left: -20, bottom: 0 },
    valueFormatter,
    xAxisTickFormatter,
    yAxisTickFormatter,
    xAxisInterval,
}: MultiSeriesChartProps) {
    const reactId = useId();

    const domain = useMemo((): [number, number] => {
        const values = data.flatMap((point) =>
            series.map((entry) => Number(point[entry.dataKey] ?? 0)),
        );
        const max = Math.max(...values, 0);
        return [
            0,
            Math.ceil(max / AXIS_TICK_COUNT) * AXIS_TICK_COUNT ||
                AXIS_TICK_COUNT,
        ];
    }, [data, series]);

    const ticks = useMemo(() => {
        const step = domain[1] / AXIS_TICK_COUNT;
        return Array.from(
            { length: AXIS_TICK_COUNT + 1 },
            (_, index) => step * index,
        );
    }, [domain]);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <RechartsAreaChart
                data={data}
                margin={margin}
                accessibilityLayer={false}
            >
                <defs>
                    {series.map((entry) => (
                        <linearGradient
                            key={entry.dataKey}
                            id={`${reactId}-${entry.dataKey}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={entry.color}
                                stopOpacity={0.35}
                            />
                            <stop
                                offset="100%"
                                stopColor={entry.color}
                                stopOpacity={0.04}
                            />
                        </linearGradient>
                    ))}
                </defs>

                {showGrid && (
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(217, 217, 255, 0.25)"
                        vertical={false}
                    />
                )}

                <XAxis
                    dataKey="date"
                    stroke="var(--grey-100)"
                    tick={{ fill: 'var(--grey-100)', fontSize: 12 }}
                    tickFormatter={xAxisTickFormatter}
                    axisLine={{ stroke: 'var(--grey-500)' }}
                    tickLine={false}
                    tickMargin={4}
                    interval={xAxisInterval ?? 'preserveStartEnd'}
                />

                <YAxis
                    stroke="var(--grey-100)"
                    tick={{ fill: 'var(--grey-100)', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    domain={domain}
                    ticks={ticks}
                    tickFormatter={yAxisTickFormatter}
                />

                <Tooltip
                    cursor={{
                        stroke: 'var(--grey-400)',
                        strokeDasharray: '3 3',
                    }}
                    content={
                        <MultiSeriesChartTooltip
                            series={series}
                            valueFormatter={valueFormatter}
                            titleFormatter={xAxisTickFormatter}
                        />
                    }
                />

                {series.map((entry) => (
                    <Area
                        key={entry.dataKey}
                        type="linear"
                        dataKey={entry.dataKey}
                        stroke={entry.color}
                        strokeWidth={2}
                        strokeOpacity={entry.dimmed ? DIMMED_OPACITY : 1}
                        fill={
                            entry.dimmed
                                ? 'transparent'
                                : `url(#${reactId}-${entry.dataKey})`
                        }
                        dot={false}
                        activeDot={entry.dimmed ? false : { r: 4 }}
                        animationDuration={500}
                        animationEasing="ease-out"
                    />
                ))}
            </RechartsAreaChart>
        </ResponsiveContainer>
    );
}
